/**
 * Community Members List Sheet Component (Read-Only)
 * Displays all community members in a sidebar sheet without editing capabilities
 */

import { Loader2, User } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useCommunityMembersInfinite } from "../hooks/useCommunityMembersInfinite"

interface CommunityMembersListSheetProps {
  communityId: string
  isOpen: boolean
  onClose: () => void
  totalMemberCount?: number
}

export function CommunityMembersListSheet({
  communityId,
  isOpen,
  onClose,
  totalMemberCount,
}: CommunityMembersListSheetProps) {
  const navigate = useNavigate()
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useCommunityMembersInfinite(communityId)

  // Get all members from all pages
  const allMembers = data?.pages.flatMap(page => page.members) ?? []
  const totalCount = totalMemberCount ?? data?.pages?.[0]?.pagination?.total ?? allMembers.length

  const handleMemberClick = (memberId: string) => {
    navigate(`/profile/${memberId}`)
  }

  return (
    <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Community Members</SheetTitle>
          <SheetDescription>
            {totalCount} {totalCount === 1 ? "member" : "members"}
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {/* Member List */}
            <div className="flex-1 overflow-hidden py-4">
              <div className="h-full overflow-y-auto">
                <div className="space-y-2">
                  {allMembers.map(member => (
                    <button
                      key={member.id}
                      type="button"
                      onClick={() => handleMemberClick(member.id)}
                      className="w-full flex items-center gap-3 rounded-lg border border-card bg-card p-3 transition-colors hover:bg-muted/50 text-left cursor-pointer"
                    >
                      {/* Avatar */}
                      <Avatar
                        src={member.coverImage ?? undefined}
                        fallback={member.name || "?"}
                        size="md"
                        className="h-10 w-10"
                      />

                      {/* User Info */}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{member.name || "No name"}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {member.username ? `@${member.username}` : "No username"}
                        </p>
                      </div>

                      {/* Role badges - show all role titles if exists */}
                      {member.roleTitles && member.roleTitles.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {member.roleTitles.map((title, index) => (
                            <span
                              key={index}
                              className="flex-shrink-0 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded"
                            >
                              {title}
                            </span>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}

                  {/* Load More Button */}
                  {hasNextPage && (
                    <div className="py-4 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          "Load more"
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Empty state */}
                  {allMembers.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12">
                      <User className="mb-2 h-12 w-12 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">No members found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
